import type { EventFormEvent, EventFormValues } from 'components/events/EventForm/types'

// docs/spec-desk-auto-translate.md: what the desk translates when a candidate
// opens. Pure, no I/O.
//
// D-03: for each pair (title, subtitle, description, price) where one side has
// text and the other is empty, translate the filled side into the empty side.
// A price with no letter is copied, not sent to DeepL (it reformats numbers).
// Free stays free with no special code: the prefill already turns '0' into two
// empty sides, so there is nothing to translate.
//
// D-04: the prefill names the subtitle sub_title/sub_title_pt while the form
// uses subTitle/subTitle_pt. The desk maps the names (toFormFill), the form
// does not.

export type DeskTranslationField =
  | 'title'
  | 'title_pt'
  | 'sub_title'
  | 'sub_title_pt'
  | 'description'
  | 'description_pt'
  | 'price'
  | 'price_pt'

export type DeskTranslationTarget = 'en' | 'pt'

export interface DeskTranslationPair {
  /** The EMPTY side that receives the text. */
  field: DeskTranslationField
  /** The filled side's text, untrimmed. */
  source: string
  /** Same target strings as TranslateButton. */
  target: DeskTranslationTarget
  /** Price with no letter: copy the source, do not call DeepL. */
  copyOnly: boolean
}

export type DeskTranslationFill = Partial<
  Pick<
    EventFormValues,
    'title' | 'title_pt' | 'subTitle' | 'subTitle_pt' | 'description' | 'description_pt' | 'price' | 'price_pt'
  >
>

export interface DeskTranslationResult {
  field: DeskTranslationField
  value: string | null
}

const PAIRS: { en: DeskTranslationField; pt: DeskTranslationField; isPrice: boolean }[] = [
  { en: 'title', pt: 'title_pt', isPrice: false },
  { en: 'sub_title', pt: 'sub_title_pt', isPrice: false },
  { en: 'description', pt: 'description_pt', isPrice: false },
  { en: 'price', pt: 'price_pt', isPrice: true }
]

const HAS_LETTER = /\p{L}/u

const sideOf = (prefill: EventFormEvent, field: DeskTranslationField) => String(prefill[field] ?? '')

export const pairsToTranslate = (prefill: EventFormEvent): DeskTranslationPair[] =>
  PAIRS.flatMap(({ en, pt, isPrice }) => {
    const enText = sideOf(prefill, en)
    const ptText = sideOf(prefill, pt)
    const enFilled = enText.trim() !== ''
    const ptFilled = ptText.trim() !== ''
    if (enFilled === ptFilled) return []

    const source = ptFilled ? ptText : enText
    return [{
      field: ptFilled ? en : pt,
      source,
      target: ptFilled ? 'en' : 'pt',
      copyOnly: isPrice && !HAS_LETTER.test(source)
    } as DeskTranslationPair]
  })

const FORM_KEY: Record<DeskTranslationField, keyof DeskTranslationFill> = {
  title: 'title',
  title_pt: 'title_pt',
  sub_title: 'subTitle',
  sub_title_pt: 'subTitle_pt',
  description: 'description',
  description_pt: 'description_pt',
  price: 'price',
  price_pt: 'price_pt'
}

export const toFormFill = (results: DeskTranslationResult[]): DeskTranslationFill =>
  results.reduce<DeskTranslationFill>((fill, { field, value }) => {
    if (value === null) return fill
    return { ...fill, [FORM_KEY[field]]: value }
  }, {})
